'use client';

import { useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Chip,
  Paper
} from '@mui/material';
import {
  Assignment,
  Approval,
  Dashboard,
  ReceiptLong,
  Analytics,
  Article,
  WarningAmber,
  ArrowForward,
  Insights,
} from '@mui/icons-material';
import Header from '../../components/HomepageContent/Header';
import FeatureHeroCtaStack from '../components/FeatureHeroCtaStack';
import FeatureResourceButtons from '../components/FeatureResourceButtons';
import Footer from '../../components/HomepageContent/Footer';
import Link from 'next/link';
import '../features-responsive.css';
import BrandName from '../../components/BrandName';

const BRAND_BLUE = '#0F4C9C';
const BRAND_GREEN = '#63A821';

const capabilities = [
  {
    icon: Article,
    title: 'Contract management',
    description: 'Capture contract dates, hourly rate, monthly/annual hour limits, the contract PDF, and CMS job-title/pay-type mapping; tasks bind seamlessly to the covering contract.',
  },
  {
    icon: Assignment,
    title: 'Task Reporting',
    description: 'Medical directors log activities online (web or mobile) with built-in validation and hour caps, then sign off the month as a formal attestation.',
  },
  {
    icon: Approval,
    title: 'Task Approval',
    description: 'Facility administrators approve or reject hours through a role-based hierarchy, then close the month to lock the record.',
  },
  {
    icon: Dashboard,
    title: 'Task Dashboard',
    description: 'A contract-centric monthly view with sign-off, close, and a signed, legally-attested Task Report PDF.',
  },
  {
    icon: ReceiptLong,
    title: 'AP Report',
    description: 'Accurate, contract-matched Accounts Payable data with seamless delivery to finance systems and reminders for incomplete facilities.',
  },
  {
    icon: Analytics,
    title: 'CMS PBJ Report',
    description: 'Quarterly physician staffing hours with CMS job codes and pay types, exportable to a CMS-ready workbook.',
  },
  {
    icon: Insights,
    title: 'Task Analysis & Compliance Dashboards',
    description: 'Company-wide and per-physician analytics that quantify engagement and ROI.',
  },
];

export default function MedicalDirectorshipPage() {
  useEffect(() => {
    document.title = 'Medical Directorship Engagement & Compliance | CareAscendium';
  }, []);

  return (
    <Box className="feature-page-wrapper" sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <Header />

      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1E4A8C 50%, #2A5A9C 100%)`,
          py: { xs: 8, md: 12 },
          px: 4,
          position: 'relative',
          overflow: 'hidden',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            label="Medical Directorship Engagement & Compliance"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 600,
              mb: 3,
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          />
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              mb: 3,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '3.25rem' },
              lineHeight: 1.15,
              maxWidth: 940,
              mx: 'auto'
            }}
          >
            MEDICAL DIRECTORSHIP ENGAGEMENT & COMPLIANCE
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255,255,255,0.92)',
              maxWidth: 840,
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.05rem', md: '1.35rem' },
              mb: 5,
            }}
          >
            Financial transactions with physicians are one of — if not the most — high-risk areas for any healthcare provider. <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> keeps medical director engagement fully documented, contract-compliant, and audit-ready, from the first logged task all the way to your quarterly CMS PBJ submission.
          </Typography>
          <FeatureHeroCtaStack>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '6px',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#55901d' },
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
            <FeatureResourceButtons />
          </FeatureHeroCtaStack>
        </Container>
      </Box>

      {/* Problem / Why Every SNF Needs This */}
      <Box sx={{ py: { xs: 8, md: 10 }, px: 4, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 900, mx: 'auto', textAlign: 'center' }}>
            <WarningAmber sx={{ fontSize: 50, color: '#f57c00', mb: 2 }} />
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a1a1a', mb: 4, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
              Why every SNF needs this
            </Typography>
            <Typography sx={{ color: '#555', fontSize: '1.15rem', lineHeight: 1.8, textAlign: 'justify' }}>
              Every Medicare- and Medicaid-certified skilled nursing facility is federally required to designate a Medical Director responsible for resident care policies and the coordination of medical care (42 CFR Section 483.70(h), surveyed under F-tag F841). Facilities must prove that the medical director is genuinely engaged — and that every dollar paid reflects fair market value, a written contract, and documented services, as required under the Anti-Kickback Statute and Stark Law. <BrandName variant="light" style={{ fontSize: 'inherit' }} /> satisfies both obligations seamlessly.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* How it works / Capabilities */}
      <Box sx={{ pt: { xs: 10, md: 12 }, pb: { xs: 8, md: 10 }, px: 4, bgcolor: BRAND_GREEN }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 800, color: 'white', mb: 6, textAlign: 'center', fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
            How <BrandName variant="white" style={{ fontSize: 'inherit' }} /> modernizes medical director compliance — end to end
          </Typography>
          
          <Box 
            sx={{ 
              maxWidth: 1000, 
              mx: 'auto', 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, 
              gap: 4 
            }}
          >
            {capabilities.map((cap, i) => (
              <Paper
                key={i}
                elevation={0}
                sx={{
                  p: 4,
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: 4,
                  border: '1px solid rgba(255,255,255,0.2)',
                  transition: 'all 0.3s ease',
                  bgcolor: 'white',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                    borderColor: 'rgba(15, 76, 156, 0.3)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: 2,
                    bgcolor: 'rgba(15, 76, 156, 0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                  }}
                >
                  <cap.icon sx={{ color: BRAND_BLUE, fontSize: 26 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: '#1a1a1a' }}>
                  {cap.title}
                </Typography>
                <Typography sx={{ color: '#666', lineHeight: 1.6, fontSize: '0.95rem', flexGrow: 1 }}>
                  {cap.description}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 6, bgcolor: '#f0f4fa' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundImage: `url('/assets/osha/bgdemo.svg'), linear-gradient(113.99deg, #0F4C9C 12.55%, #000820 87.43%)`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              borderRadius: '16px',
              minHeight: '300px',
              py: 5,
              px: 3,
              textAlign: 'center',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 15px 40px rgba(0, 8, 32, 0.12)',
            }}
          >
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, fontSize: { xs: '1.75rem', md: '2.5rem' }, textTransform: 'uppercase' }}>
              Ready to Modernize Your Compliance?
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.9, lineHeight: 1.6, fontSize: '1.1rem', maxWidth: 850 }}>
              See how <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> keeps medical director engagement CMS-compliant and verifiable.
            </Typography>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 5,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '30px',
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  '&:hover': { bgcolor: '#528a1b' },
                  boxShadow: '0 4px 10px rgba(99, 168, 33, 0.2)',
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
